import { Link } from 'react-router-dom';

import { useDocumentTitle } from '../lib/useDocumentTitle';
import { SUPPORT_EMAIL, SUPPORT_LOCATION, SUPPORT_RESPONSE_TIME } from '../lib/support';

// The App Store Support URL (TOM-98): help with the app, the gateway, alarms and
// the account, and how to reach a person. Public, signed out, and plain enough
// to read on a phone. Each section carries an id so the app can link straight
// to it.
export function Support() {
  useDocumentTitle('Neomaris | Support');

  return (
    <section className="legal-prose">
      <h2>Support</h2>
      <p className="legal-date">
        Help with the Neomaris app, your boat&apos;s gateway, alarms and your account.
      </p>

      <h3 id="contact">Contact us</h3>
      <ul>
        <li>
          <strong>Email:</strong>{' '}
          <a className="link" href={`mailto:${SUPPORT_EMAIL}`}>
            {SUPPORT_EMAIL}
          </a>
        </li>
        <li>
          <strong>Based in:</strong> {SUPPORT_LOCATION}
        </li>
        <li>
          <strong>Response time:</strong> {SUPPORT_RESPONSE_TIME}
        </li>
      </ul>
      <p>
        You can also write to us with the{' '}
        <Link className="link" to="/contact">
          contact form
        </Link>
        . Tell us your boat&apos;s name and, for the app, your iPhone or iPad model, so we can help
        faster.
      </p>
      <p>
        Neomaris is not an emergency service, and support is answered by email. If anyone aboard is
        in danger, call for help on VHF channel 16 or your local emergency number.
      </p>

      <h3 id="gateway">Set up your boat&apos;s gateway</h3>
      <p>
        Neomaris runs on a small computer aboard, the gateway, with Neomaris Core installed. It
        reads your instruments and keeps watch over the boat.
      </p>
      <ol>
        <li>
          Install Neomaris Core on the boat&apos;s computer: a Raspberry Pi 4 or 5, or any Debian or
          Ubuntu machine.{' '}
          <Link className="link" to="/downloads">
            Downloads
          </Link>{' '}
          has the package and the install steps.
        </li>
        <li>
          From a phone or computer on the boat&apos;s network, open{' '}
          <code>http://&lt;boat-ip&gt;:1880</code>. Neomaris Core looks over your instruments and
          offers a link to tombolomarine.com.
        </li>
        <li>
          Follow the link, sign in, and claim the boat. It then appears on your dashboard and in the
          app.
        </li>
      </ol>
      <p>
        If the setup page says the boat needs a set-up code, press <strong>Get a new code</strong>.
        A code from a dealer or installer goes under <strong>Have a dealer code?</strong> on the
        same page.
      </p>

      <h3 id="pairing">Pair the app with your account</h3>
      <ol>
        <li>
          Open Neomaris on your iPhone or iPad and tap <strong>Pair this iPhone</strong> (or iPad).
        </li>
        <li>
          The app shows a code. Tap <strong>Open tombolomarine.com</strong>, sign in and approve the
          code. On a computer, go to{' '}
          <a className="link" href="/device">
            tombolomarine.com/device
          </a>{' '}
          and enter the code there.
        </li>
        <li>The app finishes pairing by itself and shows your boats.</li>
      </ol>
      <p>
        Every phone and tablet paired with your account is listed at{' '}
        <a className="link" href="/devices">
          tombolomarine.com/devices
        </a>
        , where you can remove one. Signing out in the app unpairs it too. No boat yet? Tap{' '}
        <strong>Explore a demo boat</strong> to look around first.
      </p>

      <h3 id="alarms">Alarms</h3>
      <ul>
        <li>
          Neomaris sets up alarms from the instruments it finds. Your boat&apos;s gateway checks
          them aboard, so they keep running when the boat loses its internet connection.
        </li>
        <li>
          While the boat is online, an alarm that sounds reaches you as a notification on your
          paired iPhone or iPad. Allow notifications for Neomaris when the app asks, or later in the
          iPhone&apos;s Settings.
        </li>
        <li>
          On your boat&apos;s Alarms page at tombolomarine.com you can add an alarm, change its
          limits or turn it off. Under <strong>Notifications</strong> you choose which alarms notify
          you, whether you hear when one clears, and how soon you are told if the boat goes offline.
        </li>
        <li>
          When an alarm sounds you can silence it for a while or acknowledge it. The app shows how
          long each lasts before the alarm can sound again.
        </li>
        <li>
          Working on the boat? Pause its alarms from the Alarms page, and resume them when you are
          done. A paused boat says so at the top of the page.
        </li>
      </ul>

      <h3 id="delete-account">Delete your account</h3>
      <p>
        Email{' '}
        <a className="link" href={`mailto:${SUPPORT_EMAIL}`}>
          {SUPPORT_EMAIL}
        </a>{' '}
        from the address you sign in with and ask us to delete your account. We then sign you out
        everywhere and erase your personal information within 30 days. A boat only you own is erased
        with your account. On a boat you share, we remove you and the other owners keep the boat and
        its data. The{' '}
        <Link className="link" to="/privacy">
          Privacy Policy
        </Link>{' '}
        says what we keep and for how long.
      </p>

      <p className="legal-back">
        <Link className="link" to="/">
          &larr; Back to home
        </Link>
      </p>
    </section>
  );
}
